import React, { useRef, useEffect, useState, useCallback } from 'react';

import ReactCrop, { Crop, PercentCrop, PixelCrop } from 'react-image-crop';

import { useContainerSize } from '../hooks/useContainerSize';
import { Box } from '../Box';

import 'react-image-crop/dist/ReactCrop.css';

import { canvasPreview } from './canvasPreview';
import { useCrop } from './useCrop';
import { useImageLoader } from './useImageLoader';
import { calculateInnerBoxDimensions } from './calculateInnerBoxDimensions';
import { useStyles } from './ImageCropper.styles';

interface ImageCropperProps {
  blob: Blob | null;
  sizeAspect?: number;
  onCropComplete?: (croppedBlob: Blob) => void;
}

/**
 * Компонент для кадрирования изображения с использованием библиотеки react-easy-crop.
 *
 * @component
 * @param {ImageComponentProps} props - Свойства для компонента ImageComponent.
 * @param {Blob | null} props.blob - Blob-объект, содержащий URL изображения в виде текста (JSON).
 * @param {number} [props.sizeAspect=1.78] - Соотношение сторон для области кадрирования (по умолчанию 1.78 примерно соответствует 16/9).
 * @param {(croppedBlob: Blob) => void} [props.onCropComplete] - Функция-колбэк, вызываемый после завершения кадрирования выбранной области изображения.
 * @returns {JSX.Element} Компонент для кадрирования изображения.
 */

export const ImageCropper = ({
  blob,
  sizeAspect = 1.78,
  onCropComplete,
}: ImageCropperProps) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useContainerSize(containerRef);

  const { imageUrl, imageSize } = useImageLoader(blob);

  const { crop, completedCrop, setCrop, setCompletedCrop, handleImageLoad } =
    useCrop();

  const { classes } = useStyles({ completedCrop });

  const toBlob = useCallback(
    (canvas: HTMLCanvasElement): Promise<Blob | null> => {
      return new Promise((resolve) => {
        canvas.toBlob(resolve);
      });
    },
    [],
  );

  const handleCropComplete = useCallback(
    async (cropElement: PixelCrop) => {
      setCompletedCrop(cropElement);

      if (
        !imgRef.current ||
        !previewCanvasRef.current ||
        !cropElement.width ||
        !cropElement.height
      ) {
        return;
      }

      await canvasPreview(
        imgRef.current,
        previewCanvasRef.current,
        cropElement,
      );

      const newBlob = await toBlob(previewCanvasRef.current);

      if (newBlob && onCropComplete) {
        onCropComplete(newBlob);
      }
    },
    [onCropComplete, setCompletedCrop, toBlob],
  );

  const [initialCropApplied, setInitialCropApplied] = useState(false);

  useEffect(() => {
    if (completedCrop && !initialCropApplied) {
      handleCropComplete(completedCrop);
      setInitialCropApplied(true);
    }
  }, [completedCrop, initialCropApplied, handleCropComplete]);

  const innerBoxDimensions = calculateInnerBoxDimensions(
    imageSize.width,
    imageSize.height,
    containerSize.width,
    containerSize.height,
  );

  const handleCropChange = (newCrop: Crop, percentCrop: PercentCrop) => {
    setCrop(percentCrop);
  };

  const getMaxCrop = (
    imageWidth: number,
    imageHeight: number,
    aspect: number,
  ): PercentCrop => {
    const imageAspect = imageWidth / imageHeight;
    if (imageAspect > aspect) {
      const cropHeight = 100;
      const cropWidth = ((aspect * imageHeight) / imageWidth) * 100;
      return {
        unit: '%',
        x: (100 - cropWidth) / 2,
        y: 0,
        width: cropWidth,
        height: cropHeight,
      };
    } else {
      const cropWidth = 100;
      const cropHeight = (imageWidth / aspect / imageHeight) * 100;
      return {
        unit: '%',
        x: 0,
        y: (100 - cropHeight) / 2,
        width: cropWidth,
        height: cropHeight,
      };
    }
  };

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;

    if (naturalWidth < 918 || naturalHeight < 516) {
      console.warn(
        `Изображение слишком маленькое: ${naturalWidth}×${naturalHeight}. Рекомендуемый минимальный размер: 918×516.`,
      );
    }

    handleImageLoad(naturalWidth, naturalHeight);

    const maxCrop = getMaxCrop(naturalWidth, naturalHeight, sizeAspect);
    setCrop(maxCrop);
    setInitialCropApplied(false);
  };

  if (!blob) return null;

  return (
    <Box
      flex="1"
      minHeight="100%"
      width="100%"
      bgcolor="bg.greyscale.primary"
      ref={containerRef}
    >
      {imageUrl && (
        <Box
          width={innerBoxDimensions.width}
          height={innerBoxDimensions.height}
          className={classes.cropContainer}
          testId="crop-container"
        >
          <ReactCrop
            crop={crop}
            onChange={handleCropChange}
            onComplete={handleCropComplete}
            aspect={sizeAspect}
            className={classes.ReactCrop}
          >
            <img
              ref={imgRef}
              alt="Crop me"
              src={imageUrl}
              onLoad={handleLoad}
              className={classes.cropImage}
              data-testid="crop-image"
            />
          </ReactCrop>
        </Box>
      )}
      {completedCrop && (
        <canvas
          ref={previewCanvasRef}
          className={classes.canvasPreview}
          data-testid="crop-canvas"
        />
      )}
    </Box>
  );
};
